import { useState, useEffect, useRef } from "react";
import { motion, useAnimationControls, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ChevronRight, ArrowLeft, CircleDot } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("home"); // home, faq, chat
  const [faqs, setFaqs] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [name, setName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [adminOnline, setAdminOnline] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const controls = useAnimationControls();
  const [dragging, setDragging] = useState(false);

  // Fetch FAQs
  useEffect(() => {
    axios.get(`${API}/chat/faqs`).then(r => setFaqs(r.data)).catch(() => {});
  }, []);

  // Check admin status
  useEffect(() => {
    const check = () => axios.get(`${API}/chat/admin-status`).then(r => setAdminOnline(r.data.online)).catch(() => {});
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, []);

  // Poll messages when in chat
  useEffect(() => {
    if (!sessionId || view !== "chat") return;
    const poll = () => axios.get(`${API}/chat/sessions/${sessionId}/messages`).then(r => setMessages(r.data)).catch(() => {});
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [sessionId, view]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startChat = async () => {
    if (!name.trim()) return;
    try {
      const res = await axios.post(`${API}/chat/sessions`, { visitor_name: name.trim() });
      setSessionId(res.data.id);
      setView("chat");
    } catch (err) {
      console.error("Start chat error:", err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/sessions/${sessionId}/messages`, { message: input.trim(), sender: "visitor" });
      setInput("");
      const res = await axios.get(`${API}/chat/sessions/${sessionId}/messages`);
      setMessages(res.data);
    } catch (err) {
      console.error("Send message error:", err);
    } finally {
      setSending(false);
    }
  };

  const handleDragEnd = (_, info) => {
    const screenW = window.innerWidth;
    const btnX = info.point.x;
    if (btnX < screenW / 2) {
      controls.start({ x: -(screenW - 56 - 48), transition: { type: "spring", stiffness: 500, damping: 30 } });
    } else {
      controls.start({ x: 0, transition: { type: "spring", stiffness: 500, damping: 30 } });
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <motion.div
          drag
          dragConstraints={{ top: -500, bottom: 0, left: -(window.innerWidth || 800), right: 0 }}
          dragElastic={0.1}
          onDragStart={() => setDragging(true)}
          onDragEnd={(e, info) => {
            setTimeout(() => setDragging(false), 150);
            handleDragEnd(e, info);
          }}
          animate={controls}
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-6 right-6 z-[60] touch-none"
          data-testid="chat-float-btn"
        >
          <button
            onClick={() => { if (!dragging) setOpen(true); }}
            className="w-14 h-14 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-[0_4px_20px_rgb(220,38,38,0.4)] relative"
          >
            <MessageCircle size={26} />
            {adminOnline && (
              <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
            )}
          </button>
        </motion.div>
      )}

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-4 z-[60] w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-[0_20px_60px_rgb(0,0,0,0.15)] border border-stone-200 flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-red-600 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                {view !== "home" && (
                  <button onClick={() => setView(view === "chat" ? "home" : "home")} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div>
                  <h3 className="font-bold text-sm">Winners Chapel Ipswich</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <CircleDot size={10} className={adminOnline ? "text-green-300" : "text-red-300"} />
                    <span className="text-[11px] opacity-90">{adminOnline ? "Support online" : "Leave a message"}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" data-testid="chat-close-btn">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {view === "home" && (
                <div className="p-5 space-y-4">
                  <p className="text-sm text-stone-600">Hi there! How can we help you today?</p>

                  {/* FAQ section */}
                  <div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Frequently Asked</p>
                    <div className="space-y-2">
                      {faqs.map((faq, i) => (
                        <button
                          key={i}
                          onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                          className="w-full text-left"
                          data-testid={`faq-item-${i}`}
                        >
                          <div className="flex items-center justify-between p-3 bg-stone-50 hover:bg-red-50 rounded-xl transition-colors">
                            <span className="text-sm font-medium text-stone-800">{faq.q}</span>
                            <ChevronRight size={14} className={`text-stone-400 transition-transform ${expandedFaq === i ? "rotate-90" : ""}`} />
                          </div>
                          <AnimatePresence>
                            {expandedFaq === i && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <p className="text-xs text-stone-500 px-3 py-2 bg-stone-50 rounded-b-xl -mt-1 border-t border-stone-100">{faq.a}</p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start chat */}
                  <div className="pt-2 border-t border-stone-100">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Chat with us</p>
                    {!sessionId ? (
                      <div className="space-y-2">
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter your name to start..."
                          className="w-full px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                          data-testid="chat-name-input"
                          onKeyDown={(e) => e.key === "Enter" && startChat()}
                        />
                        <button
                          onClick={startChat}
                          disabled={!name.trim()}
                          data-testid="chat-start-btn"
                          className="w-full bg-red-600 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40"
                        >
                          Start Chat
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setView("chat")}
                        data-testid="chat-continue-btn"
                        className="w-full bg-red-600 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors"
                      >
                        Continue Chat
                      </button>
                    )}
                  </div>
                </div>
              )}

              {view === "chat" && (
                <div className="flex flex-col h-full">
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-testid="chat-messages">
                    {messages.length === 0 && (
                      <p className="text-xs text-stone-400 text-center py-8">
                        {adminOnline ? "A helper is online. Send a message!" : "Leave a message and we'll get back to you."}
                      </p>
                    )}
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                          msg.sender === "visitor"
                            ? "bg-red-600 text-white rounded-br-md"
                            : "bg-stone-100 text-stone-800 rounded-bl-md"
                        }`}>
                          {msg.sender === "admin" && <p className="text-[10px] font-bold text-red-600 mb-0.5">Support</p>}
                          {msg.message}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
              )}
            </div>

            {/* Input bar (chat view) */}
            {view === "chat" && (
              <div className="border-t border-stone-200 px-4 py-3 shrink-0 flex gap-2 items-center">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                  data-testid="chat-message-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  data-testid="chat-send-btn"
                  className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center justify-center transition-colors disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
